import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getGaTagforL1L2 } from "@NA/Utils/GtmUtility";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

interface OwnProps {
    brandIds: string[];
    title: string;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.brandIds,
        randomize: false,
        pageSize: parseInt("16"),
        select: "id,detailPagePath,logoSmallImagePath,logoAltText,name",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
}

export const listStyles: BrandListStyles = {
    titleText: {
        variant: "h2",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-align: center;
        `,
    },
    imageItem: {
        css: css`
            justify-content: center;
            border-radius: 3px;
            border: solid 1px rgba(221, 221, 221, 0.6);
            max-width: 223px;
            height: 110px;
            margin: 0 20px 20px 0;
            display: inline;
            flex-basis: calc(16.66% - 20px) !important;
            max-width: calc(16.66% - 20px) !important;
            width: calc(16.66% - 20px) !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                       margin: 0 6px 6px 0;
                       flex-basis: 48%!important;
                       max-width: 48%!important; 
                    `,
                    css`
                       margin: 0 6px 6px 0;
                       flex-basis: 48%!important;
                       max-width: 48%!important; 
                    `,
                    css`
                       width:calc(33% - 10px) !important;
                       flex-basis: calc(33% - 10px) !important;
                       max-width: 230px !important;
                       &:nth-child(3n) {                  
                            margin-right:0px;               
                        } 
                    `,
                    null,
                    null,
                ])}
                               
        `,
    },
    link: {
        css: css`
            height: inherit;
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: 110px;
            max-height: 110px;
            img {
                object-fit: cover;
                height: 90px !important;
                max-height: 90px !important;
                padding: 0px;
                will-change: auto;
            }
        `,
    },
};

const styles = listStyles;

class ADICategoryBrands extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    render() {
        const {
            brandsState: { isLoading, value: brandList },
            brandIds,
            title,
            settingsCollection: {
                productSettings: { catalogUrlPath },
            },
        } = this.props;

        if (isLoading || !brandList) {
            return null;
        }

        const desktop = parseInt("7", 12);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);
        const catalogUrlPathString = catalogUrlPath.endsWith("/") ? catalogUrlPath.slice(0, -1) : catalogUrlPath;

        const imgProps = {
            style: {
                width: "75%",
                maxWidth: "100%",
                maxHeight: `90px`,
                height: "90px",
                margin: "0 auto",               
            },
        };

        return (
            <StyledWrapper {...styles.container} data-test-selector="brandGallery">
                <VisuallyHidden as="h1">{translate("Explore available brands")}</VisuallyHidden>
                {this.props.title && (
                    <Typography
                        variant="headerSecondary"
                        {...styles.titleText}
                        data-ga-promo={getGaTagforL1L2(this.props.title, "", "")}
                        data-test-selector="brandGalleryTitle"
                    >
                        {this.props.title}
                    </Typography>
                )}
                <FlexWrapContainer {...styles.imageContainer}>
                    {brandList.map(brand => {
                        if (brand === brandList[0]) {
                            const focusFunction = () => {
                                const elementToFocus = document.getElementById("collapseButton");
                                if (elementToFocus) {
                                    elementToFocus.focus();
                                } else {
                                    document.getElementById("expandButton")?.focus();
                                }
                            };
                            return (
                                <FlexItem
                                    key={brand.id}
                                    flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                    {...styles.imageItem}
                                >
                                    <SkipNav
                                        extendedStyles={styles.skipToContent}
                                        text={translate("Skip to full brand list")}
                                        focusFunction={focusFunction}
                                    />
                                    <Clickable href={`${catalogUrlPathString}${brand.detailPagePath}`} {...styles.link} className={getGaTagforL1L2("", "", "")}>
                                        <LazyImage
                                            imgProps={imgProps}
                                            {...styles.image}
                                            className={`${(brand.detailPagePath).split('/').slice(2)}`}
                                            src={brand.logoSmallImagePath}
                                            data-ga-promo={getGaTagforL1L2(this.props.title, brand.name, "")}
                                            altText={
                                                brand.logoAltText
                                                    ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                                    : `${translate("Go to brand page")} ${brand.name}`
                                            }
                                        />
                                    </Clickable>
                                </FlexItem>
                            );
                        }
                        return (                           
                            <FlexItem
                                key={brand.id}
                                flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                {...styles.imageItem}
                            >
                                <Clickable href={`${catalogUrlPathString}${brand.detailPagePath}`} {...styles.link} className={getGaTagforL1L2("", "", "")}>
                                    <LazyImage
                                        imgProps={imgProps}
                                        {...styles.image}
                                        src={brand.logoSmallImagePath}
                                        className={`${(brand.detailPagePath).split('/').slice(2)}`}
                                        data-ga-promo={getGaTagforL1L2(this.props.title, brand.name, "")}
                                        altText={
                                            brand.logoAltText
                                                ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                                : `${translate("Go to brand page")} ${brand.name}`
                                        }
                                    />
                                </Clickable>
                            </FlexItem>
                        );
                    })}
                </FlexWrapContainer>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ADICategoryBrands);
